import { Fragment, type ReactNode } from 'react'
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface BottomSheetProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  showCloseButton?: boolean
  /** Content rendered between the header and the scrollable area (stays fixed). */
  stickyHeader?: ReactNode
  children: ReactNode
}

export function BottomSheet({
  isOpen,
  onClose,
  title,
  showCloseButton = true,
  stickyHeader,
  children,
}: BottomSheetProps) {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-modal" onClose={onClose}>
        {/* Backdrop */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-medium"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-small"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm"
            aria-hidden="true"
          />
        </TransitionChild>

        {/* Sheet container — anchored to bottom */}
        <div className="fixed inset-0 flex items-end">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-medium"
            enterFrom="translate-y-full"
            enterTo="translate-y-0"
            leave="ease-in duration-small"
            leaveFrom="translate-y-0"
            leaveTo="translate-y-full"
          >
            <DialogPanel
              className={cn(
                'w-full max-h-[85dvh] flex flex-col',
                'bg-white rounded-t-[20px] shadow-xl',
                'transition-transform'
              )}
              style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            >
              {/* Drag handle */}
              <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
                <div className="w-10 h-1 rounded-full bg-gray-300" />
              </div>

              {/* Header */}
              {(title || showCloseButton) && (
                <div className="flex items-center justify-between px-5 pb-3 flex-shrink-0">
                  {title && (
                    <DialogTitle className="text-h3 text-gray-900">
                      {title}
                    </DialogTitle>
                  )}
                  {showCloseButton && (
                    <button
                      type="button"
                      onClick={onClose}
                      className={cn(
                        'ml-auto p-1.5 rounded-md',
                        'text-gray-400 hover:text-gray-600',
                        'hover:bg-gray-100',
                        'focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2',
                        'transition-colors duration-small'
                      )}
                      aria-label="Close"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              )}

              {/* Sticky header — above scrollable area */}
              {stickyHeader && (
                <div className="flex-shrink-0">
                  {stickyHeader}
                </div>
              )}

              {/* Content — scrollable */}
              <div className="flex-1 min-h-0 overflow-y-auto">
                {children}
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  )
}
